import jsPDF from "jspdf";
import { LIBERATION_REGULAR, LIBERATION_BOLD } from "./pdfFonts";

// Register a Unicode TTF (Turkish chars: ğ, ş, İ, ı, ö, ü, ç) — jsPDF's built-in
// helvetica uses WinAnsi encoding and drops Turkish glyphs.
const registerTurkishFonts = (doc) => {
    doc.addFileToVFS("LiberationSans-Regular.ttf", LIBERATION_REGULAR);
    doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
    doc.addFileToVFS("LiberationSans-Bold.ttf", LIBERATION_BOLD);
    doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");
};

/**
 * Generate a clean PDF receipt for a reservation.
 * `r` is the reservation object as returned by /api/customer/reservations or admin endpoints.
 * `settings` exposes company contact / IBAN info.
 * `formatPrice(value)` is the AppContext helper.
 */
export const downloadReservationPdf = ({ reservation, settings, formatPrice }) => {
    const r = reservation;
    const s = settings || {};
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    registerTurkishFonts(doc);
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 48;
    let y = margin;

    // Gold accent bar
    doc.setFillColor(212, 175, 55); // gold
    doc.rect(0, 0, pageW, 6, "F");

    // Header
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(22);
    doc.setTextColor(8, 44, 39);
    doc.text(s.company_name || "Asis Hotels", margin, y + 26);

    doc.setFont("LiberationSans", "normal");
    doc.setFontSize(9);
    doc.setTextColor(106, 96, 72);
    doc.text("Mekke & Medine Otel Rezervasyonu", margin, y + 42);
    y += 70;

    // Title
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.7);
    doc.line(margin, y, pageW - margin, y);
    y += 22;
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(16);
    doc.setTextColor(8, 44, 39);
    doc.text("REZERVASYON MAKBUZU", margin, y);
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(11);
    doc.setTextColor(176, 138, 31);
    doc.text(r.reservation_code, pageW - margin, y, { align: "right" });
    y += 8;
    doc.line(margin, y, pageW - margin, y);
    y += 24;

    // Status
    const statusLabel = { approved: "ONAYLI", pending: "BEKLEMEDE", rejected: "REDDEDILDI" }[r.status] || r.status;
    const statusColor = { approved: [16, 122, 92], pending: [176, 138, 31], rejected: [185, 28, 28] }[r.status] || [100, 100, 100];
    doc.setFillColor(...statusColor);
    doc.rect(margin, y - 12, 90, 18, "F");
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(`DURUM: ${statusLabel}`, margin + 45, y + 1, { align: "center" });
    y += 28;

    // Two-column section helper
    const fmt = (v) => (v == null ? "-" : String(v));
    const row = (label, value) => {
        doc.setFont("LiberationSans", "normal");
        doc.setFontSize(9);
        doc.setTextColor(106, 96, 72);
        doc.text(label.toUpperCase(), margin, y);
        doc.setFont("LiberationSans", "bold");
        doc.setFontSize(11);
        doc.setTextColor(8, 44, 39);
        doc.text(fmt(value), margin + 160, y);
        y += 22;
    };

    // Hotel & stay
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(12);
    doc.setTextColor(176, 138, 31);
    doc.text("KONAKLAMA DETAYLARI", margin, y);
    y += 18;
    row("Otel", r.hotel_name);
    row("Oda", `${r.room_name} x ${r.rooms_count}`);
    row("Giriş Tarihi", new Date(r.check_in).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }));
    row("Çıkış Tarihi", new Date(r.check_out).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }));
    row("Gece Sayısı", `${r.nights} gece`);
    if (r.adults) {
        const guestBits = [`${r.adults} yetişkin`];
        if (r.children > 0) guestBits.push(`${r.children} çocuk (2-9 yaş)`);
        if (r.infants > 0) guestBits.push(`${r.infants} bebek (0-2 yaş)`);
        row("Misafir", guestBits.join(", "));
    } else {
        row("Misafir Sayısı", `${r.guests} kişi`);
    }
    row("Müşteri Tipi", r.customer_type === "tour" ? "Tur Şirketi" : "Bireysel");
    if (r.meal_plan && r.meal_plan !== "none") {
        const legacy = { breakfast: "Kahvaltı", half_board: "Yarım Pansiyon", full_board: "Tam Pansiyon" };
        const mealLabel = r.meal_plan_label || legacy[r.meal_plan] || r.meal_plan;
        row("Yemek Planı", mealLabel);
    }

    y += 6;

    // Pricing
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(12);
    doc.setTextColor(176, 138, 31);
    doc.text("ÖDEME", margin, y);
    y += 18;
    const fmtPrice = (v) => (formatPrice ? formatPrice(v) : `${v} SAR`);
    row("Toplam Tutar", fmtPrice(r.total_price));
    row("Ön Ödeme (%10)", fmtPrice(r.deposit_amount));
    const remaining = (r.total_price || 0) - (r.deposit_amount || 0);
    row("Kalan (Otelde)", fmtPrice(remaining));

    // ⚠️ Prominent "Pay at hotel" box — what the user explicitly asked for
    y += 4;
    const boxH = 56;
    doc.setFillColor(255, 247, 220); // pale gold
    doc.setDrawColor(176, 138, 31);
    doc.setLineWidth(1.2);
    doc.rect(margin, y, pageW - margin * 2, boxH, "FD");
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(9);
    doc.setTextColor(140, 100, 20);
    doc.text("OTELE GİRİŞTE ÖDENECEK TAM TUTAR", margin + 14, y + 18);
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(20);
    doc.setTextColor(176, 138, 31);
    doc.text(fmtPrice(remaining), margin + 14, y + 42);
    doc.setFont("LiberationSans", "normal");
    doc.setFontSize(8);
    doc.setTextColor(106, 96, 72);
    doc.text("Ön ödeme dışında kalan bakiye check-in sırasında otele ödenir.", pageW - margin - 14, y + 42, { align: "right" });
    y += boxH + 12;

    if (s.iban) {
        y += 6;
        doc.setFont("LiberationSans", "bold");
        doc.setFontSize(12);
        doc.setTextColor(176, 138, 31);
        doc.text("ÖN ÖDEME BİLGİLERİ", margin, y);
        y += 18;
        row("Banka", s.bank_name);
        row("IBAN", s.iban);
        row("Hesap Sahibi", s.account_holder);
        row("Açıklama", r.reservation_code);
    }

    y += 6;
    doc.setFont("LiberationSans", "bold");
    doc.setFontSize(12);
    doc.setTextColor(176, 138, 31);
    doc.text("MÜŞTERİ BİLGİLERİ", margin, y);
    y += 18;
    row("Ad Soyad", r.customer_name);
    row("Telefon", r.customer_phone);
    if (r.email) row("E-posta", r.email);

    if (r.rejection_reason && r.status === "rejected") {
        y += 6;
        doc.setFont("LiberationSans", "bold");
        doc.setFontSize(12);
        doc.setTextColor(185, 28, 28);
        doc.text("RED SEBEBİ", margin, y);
        y += 16;
        doc.setFont("LiberationSans", "normal");
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        const lines = doc.splitTextToSize(r.rejection_reason, pageW - margin * 2);
        doc.text(lines, margin, y);
        y += lines.length * 14;
    }

    // Footer
    y = doc.internal.pageSize.getHeight() - 70;
    doc.setDrawColor(212, 175, 55);
    doc.line(margin, y, pageW - margin, y);
    y += 16;
    doc.setFont("LiberationSans", "normal");
    doc.setFontSize(9);
    doc.setTextColor(106, 96, 72);
    const contactBits = [
        s.whatsapp_number ? `WhatsApp: +${s.whatsapp_number}` : null,
        s.contact_email || null,
        s.company_name || null,
    ].filter(Boolean);
    doc.text(contactBits.join("   |   "), pageW / 2, y, { align: "center" });
    y += 14;
    doc.setFontSize(8);
    doc.text(`Bu makbuz ${new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} tarihinde oluşturulmuştur.`, pageW / 2, y, { align: "center" });

    doc.save(`Asis-Rezervasyon-${r.reservation_code}.pdf`);
};
